import { useEffect, useState } from "react";
import { ErrorMessage, Field } from "formik";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { ClipboardList } from "lucide-react";
import TextError from "../../forms/TextError";
import SelectMultipleWithSearch from "../Admin/Common/SelectMultipleWithSearch";
import SelectItem from "../Admin/Common/SelectItem";
import SectionCard from "./SectionCard";
import FieldLabel from "./FieldLabel";
import { httpinstance } from "../../axios/api";
import {
  OCEAN_CARGO_TYPE_OPTIONS,
  SPACE_AVAILABILITY_OPTIONS,
} from "../../utils/constant";

const INPUT_CLS =
  "h-[42px] px-3.5 focus:outline-none block w-full text-sm border border-gray-200 rounded-xl text-gray-800 bg-white transition-colors focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100 hover:border-gray-300";

const BasicCargoDetailsForm = ({ formikData, carrierList, complete, readOnly }) => {
  const { values, setFieldValue } = formikData;
  const basic = values.basicDetails || {};

  // Same commodity master used by the Air search form's GoodDetails.jsx —
  // fetched here since the ocean manual-quote form doesn't share a parent
  // list the way the air form does.
  const [commodityOptions, setCommodityOptions] = useState([]);

  useEffect(() => {
    let active = true;
    httpinstance
      .get("commodity-list")
      .then((response) => {
        if (!active) return;
        const raw =
          response?.data?.data?.data ?? response?.data?.data ?? [];
        setCommodityOptions(
          raw.map((item) => ({
            value: item?.commodityDescription,
            label: item?.commodityDescription,
          }))
        );
      })
      .catch((err) => console.error("Commodity list error:", err));
    return () => {
      active = false;
    };
  }, []);

  // Cargo type classifications (FAK, Hazardous, etc.) are just another kind
  // of commodity choice now — listed above the fetched commodity master
  // instead of their own separate dropdown. Their value is the full label
  // (not a short code) so whatever's saved on the booking is always the
  // full name, with no separate lookup needed to display it later.
  const allCommodityOptions = [...OCEAN_CARGO_TYPE_OPTIONS, ...commodityOptions];

  return (
    <SectionCard
      id="basic-section"
      icon={ClipboardList}
      title="Basic details"
      subtitle="Carrier, service and cargo classification"
      complete={complete}
    >
      <div className="oq-field-grid">
        <div>
          <FieldLabel required>Carrier</FieldLabel>
          <Field name="basicDetails.carrier">
            {() => (
              <SelectMultipleWithSearch
                options={carrierList}
                width="100%"
                name="basicDetails.carrier"
                placeholder="Select Carrier"
                value={basic.carrier || undefined}
                setFieldValue={setFieldValue}
                disabled={readOnly}
              />
            )}
          </Field>
          <ErrorMessage name="basicDetails.carrier" component={TextError} />
        </div>

        <div>
          <FieldLabel>Service Type</FieldLabel>
          <Field
            type="text"
            name="basicDetails.serviceType"
            placeholder="CY / CY"
            className={INPUT_CLS}
            disabled={readOnly}
          />
        </div>

        <div>
          <FieldLabel required>Space Availability</FieldLabel>
          <Field name="basicDetails.spaceAvailability">
            {() => (
              <SelectItem
                options={SPACE_AVAILABILITY_OPTIONS}
                width="100%"
                name="basicDetails.spaceAvailability"
                placeholder="Select availability"
                value={basic.spaceAvailability || undefined}
                setFieldValue={setFieldValue}
                disabled={readOnly}
              />
            )}
          </Field>
          <ErrorMessage name="basicDetails.spaceAvailability" component={TextError} />
        </div>

        <div>
          <FieldLabel>Transit Time</FieldLabel>
          <Field
            type="text"
            name="basicDetails.transitTime"
            placeholder="e.g. 24 days"
            className={INPUT_CLS}
            disabled={readOnly}
          />
        </div>

        <div>
          <FieldLabel>Sailing</FieldLabel>
          <Field
            type="text"
            name="basicDetails.sailing"
            placeholder="e.g. Weekly · Wed"
            className={INPUT_CLS}
            disabled={readOnly}
          />
        </div>

        <div>
          <FieldLabel>Start Date</FieldLabel>
          <Field name="basicDetails.startDate">
            {() => (
              <DatePicker
                value={basic.startDate ? dayjs(basic.startDate) : null}
                onChange={(date) => setFieldValue("basicDetails.startDate", date)}
                format="DD/MM/YYYY"
                disablePast
                disabled={readOnly}
                slotProps={{ textField: { size: "small", fullWidth: true, placeholder: "dd/mm/yyyy" } }}
              />
            )}
          </Field>
        </div>

        <div style={{ gridColumn: "span 2" }}>
          <FieldLabel>Routing Info</FieldLabel>
          <Field
            type="text"
            name="basicDetails.routingInfo"
            placeholder="Routing Info"
            className={INPUT_CLS}
            disabled={readOnly}
          />
        </div>

        <div style={{ gridColumn: "span 4" }}>
          <FieldLabel>Commodity</FieldLabel>
          <Field name="cargoDetails.commodity">
            {() => (
              <SelectMultipleWithSearch
                options={allCommodityOptions}
                width="100%"
                name="cargoDetails.commodity"
                placeholder="Search commodity"
                value={values.cargoDetails?.commodity || undefined}
                setFieldValue={setFieldValue}
                disabled={readOnly}
              />
            )}
          </Field>
        </div>
      </div>
    </SectionCard>
  );
};

export default BasicCargoDetailsForm;
